import { appointmentStatus, type ApiClient, type Appointment } from "@shyne/shared/api";
import { readEveryAppointment } from "@/repositories/appointment-records";
import type { PlannedAppointmentRow } from "./bulk-upload-plan";

type ListAppointments = ApiClient["appointments"]["list"];

const NOT_CANCELLED = appointmentStatus.options.filter((status) => status !== "cancelled");

const keyOf = (vendor: string, property: string, unit: string, service: string, day: string) => [vendor, property, unit, service, day].join("|");

export const duplicateKey = (planned: PlannedAppointmentRow) =>
  keyOf(planned.vendorOrgId, planned.propertyId, planned.unitId ?? `number:${planned.unitNumber}`, planned.serviceId, planned.appointmentDate);

const bookedKey = (appointment: Appointment) =>
  keyOf(appointment.vendor.id, appointment.property.id, appointment.unit?.id ?? "", appointment.service.id, appointment.date);

function daysByProperty(rows: PlannedAppointmentRow[]): Map<string, { dateFrom: string; dateTo: string }> {
  const windows = new Map<string, { dateFrom: string; dateTo: string }>();
  for (const { propertyId, appointmentDate } of rows) {
    const window = windows.get(propertyId);
    windows.set(propertyId, {
      dateFrom: window && window.dateFrom < appointmentDate ? window.dateFrom : appointmentDate,
      dateTo: window && window.dateTo > appointmentDate ? window.dateTo : appointmentDate,
    });
  }
  return windows;
}

export async function bookedAppointmentKeys(
  list: ListAppointments,
  candidates: PlannedAppointmentRow[],
  actingOrganization: string | null
): Promise<Set<string>> {
  const windows = daysByProperty(candidates.filter((planned) => planned.unitId));
  const bookings = await Promise.all(
    [...windows].map(([property, days]) =>
      readEveryAppointment(list, { property, ...days, status: NOT_CANCELLED, ...(actingOrganization ? { actingOrganization } : {}) })
    )
  );
  return new Set(bookings.flat().map(bookedKey));
}
